import { fromJS } from "immutable";
import { defineMessages } from "react-intl";

import api from "../api";

import { showAlert } from "./alerts";
import { me } from "../initial_state";

// Synchronisiert die lokalen Einstellungen geraeteuebergreifend ueber die private
// Notiz des eigenen Kontos. Die Einstellungen stehen dort als Block
// <sloth-settings>BASE64(JSON)</sloth-settings>; anderer Notiztext bleibt unberuehrt.
// Frueher hiess der Block <fe-settings>. Er wird weiter gelesen und beim naechsten
// Abgleich durch <sloth-settings> ersetzt.

export const LOCAL_SETTINGS_SYNC_APPLY = "LOCAL_SETTINGS_SYNC_APPLY";
export const LOCAL_SETTINGS_SYNC_STAMP = "LOCAL_SETTINGS_SYNC_STAMP";
export const LOCAL_SETTINGS_SYNC_DISABLE = "LOCAL_SETTINGS_SYNC_DISABLE";

const messages = defineMessages({
  tooLongTitle: { id: "settings.sync.too_long.title", defaultMessage: "Settings not synced" },
  tooLongMessage: { id: "settings.sync.too_long.message", defaultMessage: "Your account note is too long to hold the settings. Synchronization was turned off." },
});

// Pro Geraet, werden nie synchronisiert.
export const LOCAL_ONLY_KEYS = ["layout", "sync_settings", "sync_updated_at"];

const TAG = "sloth-settings";
const LEGACY_TAG = "fe-settings";
const BLOCK = /<(sloth-settings|fe-settings)>([A-Za-z0-9+/=]*)<\/\1>/;
const PUSH_DELAY = 3000;
const PULL_INTERVAL = 60 * 1000;

let defaults = null;
let pushTimer = null;
let lastPull = 0;

// Der Reducer meldet seine Standardwerte an (vermeidet einen Import-Zyklus).
export const registerSyncDefaults = (value) => {
  defaults = value;
};

const encode = (data) => btoa(unescape(encodeURIComponent(JSON.stringify(data))));
const decode = (text) => JSON.parse(decodeURIComponent(escape(atob(text))));

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

// Nur Abweichungen von den Standardwerten speichern, das haelt die Notiz klein.
const diff = (value, base) => {
  const out = {};

  Object.keys(value).forEach(key => {
    const current = value[key];
    const reference = isObject(base) ? base[key] : undefined;

    if (isObject(current)) {
      const nested = diff(current, reference);

      if (Object.keys(nested).length > 0) {
        out[key] = nested;
      }
    } else if (JSON.stringify(current) !== JSON.stringify(reference)) {
      out[key] = current;
    }
  });

  return out;
};

const persist = (getState) => {
  try {
    localStorage.setItem("mastodon-settings", JSON.stringify(getState().get("local_settings").toJS()));
  } catch {
    // Speicher nicht verfuegbar, nichts zu tun
  }
};

const fetchNote = async (getState) => {
  const { data } = await api(getState).get(`/api/v1/accounts/relationships?id[]=${me}`);
  return data?.[0]?.note || "";
};

const parseRemote = (note) => {
  const match = BLOCK.exec(note);

  if (!match) {
    return null;
  }

  try {
    const parsed = decode(match[2]);
    return isObject(parsed) && isObject(parsed.values) ? { ...parsed, legacy: match[1] === LEGACY_TAG } : null;
  } catch {
    return null;
  }
};

export const pushSettings = () => async (dispatch, getState) => {
  try {
    const updatedAt = Date.now();
    const current = getState().get("local_settings").toJS();

    LOCAL_ONLY_KEYS.forEach(key => delete current[key]);

    const block = `<${TAG}>${encode({ updated_at: updatedAt, values: diff(current, defaults ? defaults.toJS() : {}) })}</${TAG}>`;
    const note = await fetchNote(getState);
    const next = BLOCK.test(note) ? note.replace(BLOCK, () => block) : (note ? `${note}\n${block}` : block);

    await api(getState).post(`/api/v1/accounts/${me}/note`, { comment: next });

    dispatch({ type: LOCAL_SETTINGS_SYNC_STAMP, updatedAt });
    persist(getState);
  } catch (error) {
    // 422: die Notiz ist zu lang, dann Synchronisation abschalten (wie Nicolium)
    if (error?.response?.status === 422) {
      dispatch({ type: LOCAL_SETTINGS_SYNC_DISABLE });
      persist(getState);
      dispatch(showAlert(messages.tooLongTitle, messages.tooLongMessage));
    }
    // sonst optional; bei der naechsten Aenderung wird erneut versucht
  }
};

// Abgleich: neuere Einstellungen gewinnen. Beim ersten Aktivieren auf einem Geraet
// (sync_updated_at = 0) gelten vorhandene Einstellungen aus der Notiz.
export const syncSettings = () => async (dispatch, getState) => {
  if (!me || !getState().getIn(["local_settings", "sync_settings"])) {
    return;
  }

  lastPull = Date.now();

  try {
    const remote = parseRemote(await fetchNote(getState));
    const localStamp = getState().getIn(["local_settings", "sync_updated_at"]) || 0;

    if (remote && remote.updated_at > localStamp) {
      dispatch({ type: LOCAL_SETTINGS_SYNC_APPLY, values: remote.values, updatedAt: remote.updated_at });
      persist(getState);

      // Old block name: write the same settings back under the new name.
      if (remote.legacy) {
        await dispatch(pushSettings());
      }
    } else if (!remote || remote.legacy || remote.updated_at < localStamp) {
      await dispatch(pushSettings());
    }
  } catch {
    // offline oder Notiz nicht erreichbar
  }
};

export const scheduleSettingsPush = () => (dispatch, getState) => {
  if (!me || !getState().getIn(["local_settings", "sync_settings"])) {
    return;
  }

  clearTimeout(pushTimer);
  pushTimer = setTimeout(() => dispatch(pushSettings()), PUSH_DELAY);
};

export const initSettingsSync = () => (dispatch) => {
  dispatch(syncSettings());

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && Date.now() - lastPull > PULL_INTERVAL) {
      dispatch(syncSettings());
    }
  });
};

export const applyRemoteValues = (base, values, keep) => {
  let next = base.mergeDeep(fromJS(values));
  LOCAL_ONLY_KEYS.forEach(key => {
    next = next.set(key, keep.get(key));
  });
  return next;
};
